import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../components/ui/Card'
import { Input } from '../../components/ui/input'
import { Label } from '../../components/ui/label'
import { Button } from '../../components/ui/button'
import { completeSsoSession, verifyMfaLogin } from '../../store/actions/auth/login'
import Logo from '../../assets/images/logo-dark.svg'
import Loading from '@/shared/common/Loading'

/**
 * ORG-008 · The SSO landing page. The identity provider redirected back to
 * the API, which left a session cookie and sent the browser here; one
 * exchange turns that cookie into the app's bearer token. Accounts with a
 * second factor walk the same code screen as password sign-in.
 */
const SsoComplete = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const isLoading = useSelector((state) => state.isLoading ?? null)
  const [mfaChallenge, setMfaChallenge] = useState(null)
  const [mfaCode, setMfaCode] = useState('')
  const [failed, setFailed] = useState(false)
  const exchanged = useRef(false)

  useEffect(() => {
    // React 18 StrictMode mounts twice; the exchange must run once.
    if (exchanged.current) return
    exchanged.current = true
    dispatch(completeSsoSession({ onMfaChallenge: (challengeId) => setMfaChallenge(challengeId) }))
      .then((ok) => { if (!ok) setFailed(true) })
  }, [dispatch])

  const handleMfaSubmit = () => {
    if (!mfaCode.trim()) return
    dispatch(verifyMfaLogin({ challengeId: mfaChallenge, code: mfaCode.trim() }))
  }

  return (
    <div className='flex justify-center items-center h-screen p-4'>
      <Card className="max-w-2xl w-full relative shadow-none rounded-sm">
        <div className='flex justify-center items-center pt-4'>
          <img className='absolute -top-12 left-[30%] w-56' src={Logo} alt="logo" />
        </div>
        <CardHeader>
          <CardTitle className="text-2xl font-500">
            {mfaChallenge ? 'Two-factor code' : 'Completing sign-in'}
          </CardTitle>
          <CardDescription>
            {mfaChallenge
              ? 'Enter the 6-digit code from your authenticator app, or a recovery code.'
              : failed
                ? 'Single sign-on could not complete.'
                : 'Finishing your single sign-on…'}
          </CardDescription>
        </CardHeader>
        {mfaChallenge ? (
          <>
            <CardContent className="space-y-2">
              <div className="space-y-1">
                <Label htmlFor="mfaCode">Authentication code</Label>
                <Input
                  id="mfaCode"
                  autoFocus
                  value={mfaCode}
                  placeholder="123456"
                  onChange={(e) => setMfaCode(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleMfaSubmit() }}
                  autoComplete='one-time-code'
                />
              </div>
            </CardContent>
            <CardFooter className="flex flex-col items-center gap-2">
              <Button className="w-full" onClick={handleMfaSubmit} disabled={isLoading || !mfaCode.trim()}>
                {isLoading ? <> <Loading width={16} height={16} color="#fff" /> Verifying...</> : 'Verify'}
              </Button>
            </CardFooter>
          </>
        ) : (
          <CardContent className="flex flex-col items-center gap-4 pb-8">
            {failed ? (
              <Button variant="outline" onClick={() => navigate('/signin')}>Back to sign in</Button>
            ) : (
              <Loading />
            )}
          </CardContent>
        )}
      </Card>
    </div>
  )
}

export default SsoComplete
